import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useThemeMode } from '../theme/ThemeModeProvider';
import { GLYPH_PATH } from './brandLoaderGlyph';

/**
 * The era92 loader: the "92" mark inside three arcs whose brightness ramps up
 * in turn, rippling outwards. White in dark mode, a coral-to-pink gradient in
 * light mode.
 *
 * The numbers below were measured from the supplied loader recordings
 * (loaders/dark.mov and loaders/light.mov, 30 fps): ring radii, stroke widths
 * and arc spans in the recording's pixels, and each arc's per-frame opacity.
 * The drawing is centred on the rings, so the viewBox is symmetric.
 */
const VIEW = 112;
const FPS = 30;

interface IRing {
  radius: number;
  stroke: number;
  /** Degrees, clockwise from 3 o'clock; the arc runs from `from` to `to`. */
  from: number;
  to: number;
  /** 0-255 opacity of one loop, one value per recording frame. */
  frames: number[];
  /** Seconds into its loop that the arc starts at, so the three are staggered. */
  phase: number;
}

const RINGS: IRing[] = [
  {
    radius: 65.1,
    stroke: 7.75,
    from: 10.4,
    to: 147.6,
    frames: [
      38, 40, 47, 56, 69, 83, 100, 118, 137, 156, 175, 193, 210, 225, 237, 247,
      253,
    ],
    phase: 0.033,
  },
  {
    radius: 81.75,
    stroke: 9.5,
    from: 223.8,
    to: 436.7,
    frames: [
      38, 41, 48, 58, 72, 89, 107, 126, 147, 167, 186, 205, 221, 235, 246, 253,
    ],
    phase: 0.267,
  },
  {
    radius: 102.75,
    stroke: 11.5,
    from: 261.2,
    to: 392.8,
    frames: [
      38, 40, 45, 53, 63, 75, 89, 105, 121, 138, 155, 172, 188, 204, 218, 230,
      240, 248, 253,
    ],
    phase: 0.267,
  },
];

const point = (radius: number, degrees: number) => {
  const a = (degrees * Math.PI) / 180;
  return `${(radius * Math.cos(a)).toFixed(2)} ${(radius * Math.sin(a)).toFixed(
    2,
  )}`;
};

const arcPath = ({ radius, from, to }: IRing) =>
  `M${point(radius, from)}A${radius} ${radius} 0 ${
    to - from > 180 ? 1 : 0
  } 1 ${point(radius, to)}`;

/** Opacity keyframes for one loop: the recording's values, then a reset. */
const loopKeyframes = (frames: number[]) => {
  const out: Record<string, { opacity: number }> = {};
  frames.forEach((value, i) => {
    out[`${((i / frames.length) * 100).toFixed(3)}%`] = {
      opacity: Number((value / 255).toFixed(3)),
    };
  });
  out['100%'] = { opacity: 1 };
  return out;
};

const useStyles = makeStyles(() => {
  const styles: Record<string, unknown> = {
    '@keyframes brandLoaderIn': {
      from: { opacity: 0, transform: 'scale(0.94)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    root: {
      display: 'block',
      flexShrink: 0,
      animation: '$brandLoaderIn 0.35s ease-out both',
    },
  };
  RINGS.forEach((ring, i) => {
    styles[`@keyframes brandLoaderRing${i}`] = loopKeyframes(ring.frames);
    styles[`ring${i}`] = {
      animation: `$brandLoaderRing${i} ${(ring.frames.length / FPS).toFixed(
        3,
      )}s linear infinite`,
      // Respect people who ask for less motion: hold the arcs lit instead.
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        opacity: 0.85,
      },
    };
  });
  return styles as Record<string, any>;
});

let instances = 0;

interface IProps {
  /** Width and height in px. */
  size?: number;
  className?: string;
  /** Read out by screen readers. */
  label?: string;
}

const BrandLoader: React.FC<IProps> = ({
  size = 72,
  className,
  label = 'Loading',
}) => {
  const classes = useStyles();
  const { mode } = useThemeMode();
  const gradientId = useMemo(() => {
    instances += 1;
    return `brand-loader-${instances}`;
  }, []);
  const paint = mode === 'dark' ? '#ffffff' : `url(#${gradientId})`;

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`${-VIEW} ${-VIEW} ${VIEW * 2} ${VIEW * 2}`}
      width={size}
      height={size}
      className={[classes.root, className].filter(Boolean).join(' ')}
    >
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={-75}
          y1={0}
          x2={108}
          y2={0}
        >
          <stop offset="0" stopColor="#ee4f4b" />
          <stop offset="1" stopColor="#f03663" />
        </linearGradient>
      </defs>
      {RINGS.map((ring, i) => (
        <path
          key={ring.radius}
          d={arcPath(ring)}
          fill="none"
          stroke={paint}
          strokeWidth={ring.stroke}
          strokeLinecap="round"
          className={classes[`ring${i}`]}
          style={{ animationDelay: `-${ring.phase}s` }}
        />
      ))}
      <path d={GLYPH_PATH} fill={paint} fillRule="evenodd" />
    </svg>
  );
};

export default BrandLoader;
